import i18n, { t } from "i18next";
import axios from "axios";
import { useContext, useEffect, useRef, useState } from "react";
import {
    DocumentDownload,
    DocumentUpload,
    Profile2User,
} from "iconsax-react";
import DBButton from "../../component/global/DBButton";
import DBSelect from "../../component/global/DBSelect";
import DBTable from "../../component/global/DBTable/DBTable";
import DBPagination from "../../component/global/DBPagination";
import DBSpinner from "../../component/global/DBSpinner";
import { ToastContext } from "../../component/global/ToastContext";
import { surveyCampaignUrl } from "../../helperData/ApiAddress";
import {
    CAMPAIGN_KIND,
    campaignIsEditable,
    recipientStatusLabel,
} from "./constants";
import { downloadRecipientsTemplate } from "./recipientTemplateDownload";
import { translateServerDetail } from "./translateServerDetail";

const TEMPLATE_FORMAT = {
    CSV: "csv",
    XLSX: "xlsx",
    SQL: "sql",
};

function importErrorMessage(error) {
    const data = error?.response?.data;
    if (!data) return t("survey.recipients.import.error");
    if (typeof data.detail === "string") return translateServerDetail(data.detail, t);
    if (typeof data.rows === "string") return data.rows;
    if (Array.isArray(data.rows)) return data.rows.join(" ");
    if (Array.isArray(data.non_field_errors)) {
        return data.non_field_errors.join(" ");
    }
    return t("survey.recipients.import.error");
}

export default function CampaignRecipientsSection({
    campaign,
    onChange,
    onEnsureCampaign,
    showHeader = true,
}) {
    const toast = useContext(ToastContext);
    const fileInputRef = useRef(null);
    const campaignIdRef = useRef(campaign?.id);
    const [recipients, setRecipients] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [templateFormat, setTemplateFormat] = useState(TEMPLATE_FORMAT.CSV);
    const [listLoading, setListLoading] = useState(false);
    const [importLoading, setImportLoading] = useState(false);
    const [testLoading, setTestLoading] = useState(false);
    const [populateLoading, setPopulateLoading] = useState(false);

    const campaignId = campaign?.id;
    const kind = campaign?.kind;
    const isUserCampaign =
        kind === CAMPAIGN_KIND.USERS || kind === CAMPAIGN_KIND.IN_APP;
    const canTestSend =
        kind === CAMPAIGN_KIND.EMAIL || kind === CAMPAIGN_KIND.SMS;
    const canEdit = Boolean(campaignId) && campaignIsEditable(campaign);
    const canImport = canEdit && !isUserCampaign;
    const showFileTools = !isUserCampaign;
    const canUpload = showFileTools && (!campaignId || canImport);
    const canPopulate = isUserCampaign && (!campaignId || canEdit);
    campaignIdRef.current = campaignId;

    const populateCaptionKey = {
        org: "survey.recipients.populate.org",
        public: "survey.recipients.populate.public",
        special: "survey.recipients.populate.special",
    }[campaign?.user_group_type];

    const authHeaders = () => ({
        Authorization: `Bearer ${sessionStorage.getItem("auth")}`,
        "Accept-Language": i18n.language || "fa",
    });

    useEffect(() => {
        if (!campaignId) {
            setRecipients([]);
            setTotalCount(0);
            setCurrentPage(1);
            return;
        }
        setListLoading(true);
        axios
            .get(surveyCampaignUrl(campaignId, "recipients"), {
                headers: authHeaders(),
                params: { page: currentPage },
            })
            .then((r) => {
                setRecipients(r.data?.results || []);
                setTotalCount(r.data?.count || 0);
            })
            .catch(() => setRecipients([]))
            .finally(() => setListLoading(false));
    }, [campaignId, currentPage]);

    const reloadRecipients = (id = campaignIdRef.current) => {
        if (!id) return;
        setCurrentPage(1);
        setListLoading(true);
        axios
            .get(surveyCampaignUrl(id, "recipients"), {
                headers: authHeaders(),
                params: { page: 1 },
            })
            .then((r) => {
                setRecipients(r.data?.results || []);
                setTotalCount(r.data?.count || 0);
            })
            .catch(() => setRecipients([]))
            .finally(() => setListLoading(false));
    };

    const handleDownloadTemplate = () => {
        try {
            downloadRecipientsTemplate(templateFormat);
        } catch {
            toast(t("survey.recipients.template.error"), "danger");
        }
    };

    const ensureCampaignId = () => {
        if (campaignIdRef.current) {
            return Promise.resolve(campaignIdRef.current);
        }
        if (!onEnsureCampaign) return Promise.resolve(null);
        return Promise.resolve(onEnsureCampaign()).then((saved) => {
            if (saved?.id) {
                campaignIdRef.current = saved.id;
                return saved.id;
            }
            return null;
        });
    };

    const handleImportFile = (event) => {
        const file = event.target.files?.[0];
        const id = campaignIdRef.current;
        if (!file || !id) return;
        setImportLoading(true);
        const formData = new FormData();
        formData.append("file", file);
        axios
            .post(
                surveyCampaignUrl(id, "import-recipients"),
                formData,
                { headers: authHeaders() }
            )
            .then((r) => {
                const data = r.data || {};
                toast(
                    t("survey.recipients.import.success", {
                        created: data.created ?? 0,
                        skipped: data.skipped_duplicates ?? 0,
                        suppressed: data.suppressed ?? 0,
                    }),
                    "success"
                );
                setCurrentPage(1);
                reloadRecipients();
                onChange?.();
            })
            .catch((e) => toast(importErrorMessage(e), "danger"))
            .finally(() => {
                setImportLoading(false);
                event.target.value = "";
            });
    };

    const handlePopulateUsers = () => {
        ensureCampaignId()
            .then((id) => {
                if (!id) return;
                setPopulateLoading(true);
                return axios
                    .post(
                        surveyCampaignUrl(id, "populate-users"),
                        {},
                        { headers: authHeaders() }
                    )
                    .then((r) => {
                        toast(
                            t("survey.recipients.populate.success", {
                                created: r.data?.created ?? 0,
                            }),
                            "success"
                        );
                        reloadRecipients();
                        onChange?.();
                    })
                    .catch((e) => {
                        const detail = translateServerDetail(
                            e.response?.data?.detail,
                            t
                        );
                        toast(
                            detail || t("survey.recipients.import.error"),
                            "danger"
                        );
                    })
                    .finally(() => setPopulateLoading(false));
            })
            .catch(() => {});
    };

    const handleTestSend = () => {
        if (!campaignId) return;
        setTestLoading(true);
        axios
            .post(
                surveyCampaignUrl(campaignId, "test-send"),
                {},
                { headers: authHeaders() }
            )
            .then(() => toast(t("survey.campaign.test.success"), "success"))
            .catch((e) => {
                const detail = translateServerDetail(e.response?.data?.detail, t);
                toast(detail || t("survey.campaign.test.error"), "danger");
            })
            .finally(() => setTestLoading(false));
    };

    const headers = [
        {
            id: "name",
            title: t("survey.recipients.table.name"),
            gridTemplateColumns: "1.2fr",
        },
        {
            id: "contact",
            title: t("survey.recipients.table.contact"),
            gridTemplateColumns: "1.6fr",
        },
        {
            id: "status",
            title: t("survey.recipients.table.status"),
            gridTemplateColumns: "0.9fr",
        },
    ];

    const rows = recipients.map((recipient) => ({
        name: recipient.name || "—",
        contact: recipient.email || recipient.phone_number || "—",
        status: recipientStatusLabel(recipient.status),
    }));

    const formatOptions = [
        {
            value: TEMPLATE_FORMAT.CSV,
            label: t("survey.recipients.template.format.csv"),
        },
        {
            value: TEMPLATE_FORMAT.XLSX,
            label: t("survey.recipients.template.format.xlsx"),
        },
        {
            value: TEMPLATE_FORMAT.SQL,
            label: t("survey.recipients.template.format.sql"),
        },
    ];

    return (
        <div className="campaign-recipients-section">
            {showHeader && (
                <div className="campaign-recipients-section-header">
                    <h4>{t("survey.recipients.title")}</h4>
                    <p>{t("survey.recipients.import.help")}</p>
                </div>
            )}

            <div className="surveys-toolbar recipients-toolbar">
                {canPopulate ? (
                    <DBButton
                        type="secondary"
                        outlined
                        loading={populateLoading}
                        caption={
                            <>
                                <Profile2User size={18} aria-hidden="true" />{" "}
                                {t(
                                    populateCaptionKey ||
                                        "survey.recipients.populate.all"
                                )}
                            </>
                        }
                        onClick={handlePopulateUsers}
                    />
                ) : null}

                {showFileTools && (
                    <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xlsx,.sql,text/csv,text/plain,application/sql,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            hidden
                            onChange={handleImportFile}
                        />
                        <div className="recipients-format-control">
                            <label htmlFor="recipients-template-format">
                                {t("survey.recipients.template.format")}
                            </label>
                            <DBSelect
                                id="recipients-template-format"
                                size="large"
                                aria-label={t(
                                    "survey.recipients.template.format"
                                )}
                                value={templateFormat}
                                onChange={setTemplateFormat}
                                options={formatOptions}
                                style={{ minWidth: 120 }}
                            />
                        </div>
                        <DBButton
                            type="secondary"
                            outlined
                            caption={
                                <>
                                    <DocumentDownload
                                        size={18}
                                        aria-hidden="true"
                                    />{" "}
                                    {t("survey.recipients.template.download")}
                                </>
                            }
                            onClick={handleDownloadTemplate}
                        />
                        <DBButton
                            type="secondary"
                            outlined
                            loading={importLoading}
                            disabled={!canUpload}
                            caption={
                                <>
                                    <DocumentUpload
                                        size={18}
                                        aria-hidden="true"
                                    />{" "}
                                    {t("survey.recipients.import.action")}
                                </>
                            }
                            onClick={() => {
                                if (!canUpload) return;
                                ensureCampaignId()
                                    .then((id) => {
                                        if (id) {
                                            fileInputRef.current?.click();
                                        }
                                    })
                                    .catch(() => {});
                            }}
                        />
                        {canTestSend && canEdit && (
                            <DBButton
                                type="secondary"
                                outlined
                                loading={testLoading}
                                caption={t("survey.campaign.action.test")}
                                onClick={handleTestSend}
                            />
                        )}
                    </>
                )}
            </div>

            {!campaignId && !onEnsureCampaign ? (
                <p className="survey-form-help campaign-recipients-hint">
                    {t("survey.recipients.save.first")}
                </p>
            ) : !campaignId ? (
                <div className="surveys-empty recipients-empty">
                    {t("survey.recipients.empty")}
                </div>
            ) : listLoading ? (
                <DBSpinner tip={t("loading")} />
            ) : recipients.length === 0 ? (
                <div className="surveys-empty recipients-empty">
                    {t("survey.recipients.empty")}
                </div>
            ) : (
                <>
                    <div className="surveys-table-wrap">
                        <DBTable headers={headers} rows={rows} />
                    </div>
                    <DBPagination
                        current={currentPage}
                        total={totalCount}
                        onChange={(page) => setCurrentPage(page)}
                    />
                </>
            )}
        </div>
    );
}

export const ADDRESS_CAMPAIGN_KINDS = [
    CAMPAIGN_KIND.EMAIL,
    CAMPAIGN_KIND.SMS,
    CAMPAIGN_KIND.IN_APP,
    CAMPAIGN_KIND.USERS,
    CAMPAIGN_KIND.CSV,
    CAMPAIGN_KIND.PHONE,
];
